import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CancelIcon from "@mui/icons-material/Cancel";
import { Alert, CircularProgress, IconButton } from "@mui/material";
import { useMemo, useRef, useState } from "react";

import { AutoToggle } from "@/AgentThread/AgentThreadSidebar/AgentThreadInputBox/AutoToggle.tsx";
import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { ClientToolSettingsModal } from "@/AgentThread/AgentThreadSidebar/ClientToolSettingsModal";
import { useAuth } from "@/providers";
import { ModelProfileSelector } from "@/shared/components/ModelProfileSelector";
import { BROWSER_CLIENT_TOOLS } from "@/shared/domain/agentThread/clientTools";
import { useModelProfiles } from "@/shared/domain/agentThread/hooks/useModelProfiles";
import { useInvokeSkill, useSkills } from "@/shared/domain/skills/hooks";
import { parseSlugs, resolveSlugs } from "@/shared/domain/skills/slugParse";

import { TimeFormatSettingsModal } from "../timeFormat/TimeFormatSettingsModal";

import styles from "./AgentThreadInputBox.module.css";
import { AIContextSettingsModal } from "./AIContextSettingsModal";
import { ContextWidget } from "./ContextWidget";
import { InvokeSkillButton } from "./InvokeSkillButton";
import { MoreOptionsButton } from "./MoreOptionsButton";
import {
  SkillSlugComposer,
  SkillSlugComposerHandle,
} from "./SkillSlugComposer";

interface AgentThreadInputBoxProps {
  onSubmit: (message: string) => Promise<void>;
  onCancel: () => void | Promise<void>;
  loading: boolean;
  isCancelling?: boolean;
  agentThreadModelProfile?: string;
  /** Called when sending a skill-only message creates a new thread. The parent
   * should set the returned session id as the active thread. */
  onAgentThreadCreatedFromSkill?: (sessionId: string) => void;
}

/**
 * AgentThread composer. Plain text sends flow through ``onSubmit``; messages that
 * contain one or more ``/skill-name`` slugs flow through :func:`useInvokeSkill`,
 * which delivers ``message`` + a resolved ``invoke_skills`` list to the
 * backend so the thread-state machine fabricates ``tool_use`` / ``tool_result``
 * pairs for each slug.
 */
export const AgentThreadInputBox = ({
  onSubmit,
  onCancel,
  loading,
  isCancelling = false,
  agentThreadModelProfile,
  onAgentThreadCreatedFromSkill,
}: AgentThreadInputBoxProps) => {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const {
    context,
    contextDisplayNames,
    handleContextDelete,
    autoEnabled,
    handleToggleAuto,
    modelProfile,
    setModelProfile,
    currentThreadId,
  } = useAIAgentThread();

  const { data: profiles = [] } = useModelProfiles();
  const skillsQuery = useSkills(orgId, "personal");
  const invokeSkill = useInvokeSkill(orgId);

  // Resolve the active profile: existing thread's profile, user selection, or API default
  const defaultProfileId = profiles.find((p) => p.is_default)?.id;
  const activeProfile =
    agentThreadModelProfile ?? modelProfile ?? defaultProfileId ?? "";
  const isProfileLocked = agentThreadModelProfile !== undefined;
  const [message, setMessage] = useState("");
  const [toolSettingsOpen, setToolSettingsOpen] = useState(false);
  const [contextSettingsOpen, setContextSettingsOpen] = useState(false);
  const [timeFormatSettingsOpen, setTimeFormatSettingsOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const composerRef = useRef<SkillSlugComposerHandle | null>(null);

  const skills = useMemo(
    () => skillsQuery.data?.items ?? [],
    [skillsQuery.data],
  );

  // Live resolution so canSend reflects "any slug actually invokes something"
  // — preventing a Send with only unresolved slugs from sending zero skills.
  const resolvedInvocations = useMemo(() => {
    const resolved = resolveSlugs(parseSlugs(message), skills);
    return resolved
      .filter((s) => s.skill !== null)
      .map((s) => ({ skill_id: s.skill!.skill_id, version: s.version }));
  }, [message, skills]);

  const trimmedText = message.trim();
  const canSend =
    !loading &&
    !isCancelling &&
    (trimmedText !== "" || resolvedInvocations.length > 0);

  function focusTextarea() {
    const ta = textareaRef.current;
    if (ta !== null) {
      ta.focus();
    }
  }

  function handleInsertSlash() {
    const ta = textareaRef.current;
    if (ta === null) {
      // Defensive: ref not attached yet (initial render). Just prepend a slash.
      setMessage((prev) => (prev === "" ? "/" : `${prev} /`));
      requestAnimationFrame(focusTextarea);
      return;
    }
    const cursor = ta.selectionEnd ?? message.length;
    const before = message.slice(0, cursor);
    const after = message.slice(cursor);
    // Anchor: the parser only recognises slashes preceded by start-of-string
    // or whitespace. If the previous character is neither, prepend a space so
    // the inserted slash actually triggers autocomplete.
    const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
    const insertion = needsLeadingSpace ? " /" : "/";
    const next = before + insertion + after;
    setMessage(next);
    requestAnimationFrame(() => {
      const taNow = textareaRef.current;
      if (taNow === null) {
        return;
      }
      const newCursor = before.length + insertion.length;
      taNow.focus();
      taNow.setSelectionRange(newCursor, newCursor);
      // Imperatively re-check autocomplete state — programmatic value changes
      // don't fire the textarea's onChange, so without this the popper only
      // opens on the next user keystroke. (Chrome / Safari were particularly
      // unforgiving; Firefox happened to fire a synthetic input event.)
      composerRef.current?.openAutocompleteAtCaret();
    });
  }

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    if (modelProfile === undefined && defaultProfileId) {
      setModelProfile(defaultProfileId);
    }

    if (resolvedInvocations.length === 0) {
      void onSubmit(message);
      setMessage("");
      return;
    }

    const dispatch =
      currentThreadId !== undefined
        ? invokeSkill.mutateAsync({
            chatId: currentThreadId,
            requests: resolvedInvocations,
            message: trimmedText || undefined,
          })
        : invokeSkill.mutateAsync({
            chatId: null,
            requests: resolvedInvocations,
            message: trimmedText || undefined,
            clientContext: context,
            modelProfile: agentThreadModelProfile ?? modelProfile,
            clientTools: BROWSER_CLIENT_TOOLS,
          });
    void dispatch
      .then((result) => {
        if (result.newChat) {
          onAgentThreadCreatedFromSkill?.(result.newChat.thread_id);
        }
        setMessage("");
      })
      .catch(() => {
        // Surfaced via the `invokeSkill.isError` Alert rendered below.
        // Preserve the user's text so they can correct and resubmit (e.g.
        // a non-existent @vN suffix → fix the version and retry).
      });
  };

  return (
    <div className={styles.centeringContainer}>
      <div className={styles.topContainer}>
        <ContextWidget
          context={context}
          displayNames={contextDisplayNames}
          onDelete={handleContextDelete}
        />

        <div className={styles.textFieldContainer}>
          {invokeSkill.isError && (
            <Alert
              severity="error"
              onClose={() => invokeSkill.reset()}
              className={styles.errorAlert}
            >
              {invokeSkill.error?.message ?? "Couldn't invoke skill."}
            </Alert>
          )}
          <SkillSlugComposer
            ref={composerRef}
            value={message}
            onChange={setMessage}
            onSubmit={handleSend}
            placeholder="Say something — or type / to invoke a skill"
            disabled={loading || isCancelling}
            skills={skills}
            textareaRef={textareaRef}
          />
          <div className={styles.controlsRow}>
            <div className={styles.controlsLeft}>
              <MoreOptionsButton
                onOpenToolSettings={() => setToolSettingsOpen(true)}
                onOpenContextSettings={() => setContextSettingsOpen(true)}
                onOpenTimeFormatSettings={() => setTimeFormatSettingsOpen(true)}
              />
              <AutoToggle enabled={autoEnabled} onToggle={handleToggleAuto} />
              <InvokeSkillButton onTriggerAutocomplete={handleInsertSlash} />
            </div>
            {loading && (
              <IconButton
                size="small"
                aria-label={isCancelling ? "Cancelling..." : "Cancel message"}
                onClick={() => void onCancel()}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <CircularProgress size={16} />
                ) : (
                  <CancelIcon className={styles.smallIcon} />
                )}
              </IconButton>
            )}
            {!loading && (
              <div className={styles.sendGroup}>
                <ModelProfileSelector
                  profiles={profiles}
                  value={activeProfile}
                  onChange={setModelProfile}
                  disabled={isProfileLocked}
                />
                <IconButton
                  size="small"
                  onClick={handleSend}
                  disabled={!canSend}
                  aria-label="Send message"
                >
                  <ArrowUpwardIcon className={styles.smallIcon} />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>

      <ClientToolSettingsModal
        open={toolSettingsOpen}
        onClose={() => setToolSettingsOpen(false)}
      />
      <AIContextSettingsModal
        open={contextSettingsOpen}
        onClose={() => setContextSettingsOpen(false)}
      />
      <TimeFormatSettingsModal
        open={timeFormatSettingsOpen}
        onClose={() => setTimeFormatSettingsOpen(false)}
      />
    </div>
  );
};
